import { createFileRoute } from "@tanstack/react-router";
import { queryOptions } from "@tanstack/react-query";
import { seoHead } from "@/lib/seo/head";
import { contentHeaders } from "@/lib/seo/contentCache";
import { supabase } from "@/integrations/supabase/client";
import Blog, { type BlogPost } from "@/pages/Blog";

// Isomorphic: runs on the server during SSR (so the card grid is in the
// initial HTML, not blank until client JS fetches) and is reused from the
// React Query cache on the client, so there is no second fetch after
// hydration. Mirrors the pattern in src/lib/blogQueries.ts for /blog/$slug -
// this route never had it, so every visit rendered a blank grid until a
// client-only useEffect fetch resolved.
const blogListQueryOptions = () =>
  queryOptions({
    queryKey: ["blog-list-posts"],
    queryFn: async (): Promise<BlogPost[]> => {
      const { data } = await supabase
        .from("blog_posts")
        .select("id, title, slug, excerpt, word_count, cover_image_url, category, tags, published_at")
        .eq("status", "published")
        .lte("published_at", new Date().toISOString())
        .order("published_at", { ascending: false });
      return (data as BlogPost[]) ?? [];
    },
  });

export const Route = createFileRoute("/blog/")({
  loader: async ({ context }) => {
    const posts = await context.queryClient.ensureQueryData(blogListQueryOptions());
    return { posts };
  },
  head: () => seoHead("/blog/"),
  // The listing must pick up newly published posts without a deploy.
  headers: () => contentHeaders(true),
  component: Blog,
});
